import Konva from 'konva';
import type { ComponentDef } from '../../core/library/component-def.js';
import { addUnifiedValueLabel } from './value-label.js';

/**
 * Renderer del cuerpo de un condensador cerámico tipo disco / lenteja.
 * Elipse rellena con degradado radial suave para dar volumen. Etiqueta
 * de valor centrada, envuelta en grupo con contra-transformación para
 * mantenerla legible en cualquier orientación / flip del componente.
 */
export function renderCeramicDiscBody(
  def: ComponentDef,
  labelText: string,
  rotationDeg: number = 0,
  flipped: boolean = false,
): Konva.Group {
  const g = new Konva.Group();

  const width = def.body.length_mm ?? 7.62;   // eje horizontal
  const height = def.body.width_mm ?? 5.08;   // eje vertical
  const bodyColor = def.appearance.body_color;
  const light = mix(bodyColor, '#ffffff', 0.35);
  const dark = mix(bodyColor, '#000000', 0.3);
  // Contorno con el color del cuerpo más oscuro (tono cerámico
  // saturado), no negro puro.
  const outline = mix(bodyColor, '#000000', 0.5);

  const ellipse = new Konva.Ellipse({
    x: 0,
    y: 0,
    radiusX: width / 2,
    radiusY: height / 2,
    fillRadialGradientStartPoint: { x: -width * 0.15, y: -height * 0.2 },
    fillRadialGradientStartRadius: 0,
    fillRadialGradientEndPoint: { x: 0, y: 0 },
    fillRadialGradientEndRadius: Math.max(width, height) * 0.55,
    fillRadialGradientColorStops: [0, light, 0.55, bodyColor, 1, dark],
    stroke: outline,
    strokeWidth: 0.12,
    listening: true,
  });
  ellipse.setAttr('nodeKind', 'body');
  g.add(ellipse);

  // Etiqueta con el valor, contra-transformada para orientación fija.
  const target = Math.abs(rotationDeg % 180) === 0 ? 0 : 90;
  const ps = flipped ? -1 : 1;
  const labelGroup = new Konva.Group({
    rotation: ps * (target - rotationDeg),
    scaleY: ps,
    listening: false,
  });
  const fontSize = def.appearance.label?.font_size_mm ?? Math.min(width, height) * 0.28;
  addUnifiedValueLabel(labelGroup, labelText, fontSize, width * 0.9);
  g.add(labelGroup);

  return g;
}

function mix(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  return `#${toHex(ca.r + (cb.r - ca.r) * t)}${toHex(ca.g + (cb.g - ca.g) * t)}${toHex(ca.b + (cb.b - ca.b) * t)}`;
}
function parseHex(hex: string): { r: number; g: number; b: number } {
  let s = hex.replace('#', '');
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  return {
    r: parseInt(s.slice(0, 2), 16),
    g: parseInt(s.slice(2, 4), 16),
    b: parseInt(s.slice(4, 6), 16),
  };
}
function toHex(n: number): string {
  const v = Math.round(Math.max(0, Math.min(255, n)));
  return v.toString(16).padStart(2, '0');
}
